// ============================================================
// TRAIT CATALOG - Data-driven registry of all trait definitions.
// Each trait has a unique ID, display name, description,
// and an array of TalentEffect descriptors.
// ============================================================
import type { TalentEffect } from './talentEffects';
import type { TalentInstance } from './types';

export interface TraitDefinition {
  id: string;
  name: string;
  description: string;
  mechanical: boolean;
  parameterized: boolean;
  parameterLabel?: string;
  /**
   * May be taken more than once, each with its own parameter. Left off
   * for entries whose effects are SUMMED or read first-only, where a
   * second copy would double a number or be silently ignored.
   */
  repeatable?: boolean;
  effects: TalentEffect[];
  /**
   * Talents automatically conferred by this trait (e.g. the Astartes
   * package). Resolved through getEffectiveTalents() - an explicitly
   * taken talent always wins over a granted copy of the same id.
   */
  grants?: TalentInstance[];
}

/**
 * TRAIT_CATALOG - Core Rulebook trait definitions.
 * Keyed by trait ID for O(1) lookup. Sorted alphabetically.
 */
export const TRAIT_CATALOG: Record<string, TraitDefinition> = {
  amorphous: {
    id: 'amorphous',
    name: 'Amorphous',
    description:
      'Has no fixed shape. Switches to amorphous body type (single hit zone). Immune to called shots and crit effects that sever or destroy limbs.',
    mechanical: true,
    parameterized: false,
    effects: [
      { type: 'body_type', bodyType: 'amorphous' },
      { type: 'narrative', description: 'No fixed shape - called shots have no additional effect.' },
      { type: 'narrative', description: 'Immune to crit effects that sever or destroy limbs.' },
    ],
  },

  astartes: {
    id: 'astartes',
    name: 'Astartes',
    description:
      'The full Space Marine package: gene-seed organ implants plus a suite of granted talents ' +
      '(Ambidextrous, Killing Strike, True Grit, Nerves of Steel, Quick Draw, Bulging Biceps, ' +
      'Heightened Senses, Unarmed Master, Astartes Weapon Training, Resistance: Psychic Powers) ' +
      'and Unnatural Strength & Toughness (×2). Deathwatch Training is chapter membership - ' +
      'add that talent separately for Deathwatch marines.',
    mechanical: true,
    parameterized: false,
    grants: [
      { talentId: 'ambidextrous' },
      { talentId: 'killing_strike' },
      { talentId: 'true_grit' },
      { talentId: 'nerves_of_steel' },
      { talentId: 'quick_draw' },
      { talentId: 'bulging_biceps' },
      { talentId: 'heightened_senses', parameter: 'Sight' },
      { talentId: 'heightened_senses', parameter: 'Hearing' },
      { talentId: 'unarmed_master' },
      { talentId: 'astartes_weapon_training' },
      // deathwatch_training is deliberately NOT granted (v6.36.0): it is
      // chapter membership, not gene-seed - add the talent per character
      // (the Talents tab nudges when the Astartes trait is present).
      { talentId: 'resistance', parameter: 'Psychic Powers' },
    ],
    effects: [
      { type: 'condition_immunity', conditions: ['BL', 'BLS'] },
      { type: 'reroll_grant', test: 'toughness', against: 'toxic', label: 'Oolitic Kidney' },
      // Unnatural S & T ×2 - applied as a FLOOR over the character's stored
      // multipliers (a manually-set ×3 is never downgraded; v6.27.0).
      { type: 'unnatural_characteristic', characteristic: 'str', multiplier: 2 },
      { type: 'unnatural_characteristic', characteristic: 'tgh', multiplier: 2 },
      {
        type: 'narrative',
        description:
          "Larraman's Organ: Prevents the Blood Loss critical effect - except from weapons with the Warp Weapon quality.",
      },
      {
        type: 'narrative',
        description:
          'Oolitic Kidney: May re-roll any failed Toughness Test to resist poisons and toxins, including attacks with the Toxic quality.',
      },
      {
        type: 'narrative',
        description:
          'Multi-lung: May re-roll failed Toughness Tests for drowning or asphyxiation. +30 to Toughness Tests to resist gases, and may re-roll failed results. May breathe (oxygenated) water.',
      },
      {
        type: 'narrative',
        description:
          'Mucranoid: May re-roll any failed Toughness Test caused by temperature extremes, such as desert heat or the cold of outer space.',
      },
      {
        type: 'narrative',
        description: 'Preomnor: +20 to any Toughness Test against ingested poisons.',
      },
      {
        type: 'narrative',
        description:
          "Betcher's Gland: May spit acid - Range 3m; Damage 1d5; Pen 4; Toxic. On 3+ Degrees of Success on the BS Test, may also blind the opponent for 1d5 Rounds. At GM's discretion, may slowly chew through nearly any nonliving substance (~4 kg per minute). Some Chapters' gene-seed lacks this organ (e.g. Imperial Fists).",
      },
      {
        type: 'narrative',
        description:
          'Neuroglottis: Detect poisons/toxins by taste with a Challenging (+0) Awareness Test, or by smell with a Hard (-20) Awareness Test. +10 to Tracking Tests against an opponent he has tasted.',
      },
      {
        type: 'narrative',
        description:
          "Omophagea: By devouring a portion of an enemy, may gain access to certain information at GM's discretion, or temporarily gain a Skill as an untrained Basic Skill for Intelligence Bonus hours.",
      },
      {
        type: 'narrative',
        description:
          'Sus-An Membrane: May enter suspended animation by meditating for 1d5 Rounds; activates automatically if knocked unconscious by Critical Damage. Wounds neither deteriorate nor heal. Reviving requires chemical therapy, auto-suggestion, and a Hard (-20) Medicae Test.',
      },
      {
        type: 'narrative',
        description:
          'Catalepsean Node: No penalties to Perception-based Tests (such as Awareness) when awake for long periods of time.',
      },
      {
        type: 'narrative',
        description:
          'Progenoid Glands: Contain the gene-seed. Removal requires a Difficult (-10) Medicae Test taking 1 Round (+1 Round per Degree of Failure); Arduous (-40) and +2 Rounds without a narthecium.',
      },
      {
        type: 'narrative',
        description:
          'Other implants (no separate in-game effect): Black Carapace, Melanchromic Organ, Secondary Heart, Ossmodula, Biscopea, Haemastamen, Occulobe, Lyman’s Ear.',
      },
    ],
  },

  auto_stabilized: {
    id: 'auto_stabilized',
    name: 'Auto-stabilized',
    description: 'Can fire heavy weapons without bracing. No penalty for firing heavy weapons while standing.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'No bracing needed for heavy weapons.' }],
  },

  bestial: {
    id: 'bestial',
    name: 'Bestial',
    description: 'Animal intelligence. Cannot use equipment, weapons, or complex tools.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Animal intelligence - cannot use equipment or tools.' }],
  },

  black_carapace: {
    id: 'black_carapace',
    name: 'Black Carapace',
    description:
      'The nineteenth implant, the interface with power armour: a Space Marine so plated moves as one thing ' +
      'and presents less than his bulk - counted as one size smaller to hit, though still Hulking for everything else.',
    mechanical: true,
    parameterized: false,
    // An INNATE exposure (v8.355.0): the to-hit family reads it beside
    // anything the target wears. Not granted by the Astartes package -
    // the user's ruling names it as a separate, welcome addition.
    effects: [{ type: 'exposure', code: 'Ex:compact;size=-1' }],
  },

  blind: {
    id: 'blind',
    name: 'Blind',
    description: 'Cannot see. Immune to Blinding effects. Uses other senses to perceive surroundings.',
    mechanical: true,
    parameterized: false,
    effects: [
      { type: 'condition_immunity', conditions: ['B'] },
      { type: 'narrative', description: 'Uses other senses (tremorsense, echolocation, etc.).' },
    ],
  },

  burrower: {
    id: 'burrower',
    name: 'Burrower',
    description: 'Can burrow through solid material. Movement reminder only.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Can burrow through solid material.' }],
  },

  crawler: {
    id: 'crawler',
    name: 'Crawler',
    description: 'Moves using many legs or slithering at half normal speed. Cannot be knocked prone.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Half speed movement. Cannot be knocked prone.' }],
  },

  daemonic: {
    id: 'daemonic',
    name: 'Daemonic',
    description: 'Gains Daemonic Protection (TB bonus) bypassed only by Sanctified/Force/Warp weapons.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'Protection Rating',
    effects: [{ type: 'daemonic_protection', value: 0 }], // value set from parameter
  },

  dark_sight: {
    id: 'dark_sight',
    name: 'Dark Sight',
    description: 'Can see normally in total darkness. No penalties from poor illumination.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Sees normally in complete darkness.' }],
  },

  deadly_natural_weapons: {
    id: 'deadly_natural_weapons',
    name: 'Deadly Natural Weapons',
    description: 'Natural weapons deal 1d10 damage instead of 1d5. Configure on the weapon entry.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Natural weapons use d10 instead of d5 for damage.' }],
  },

  fear: {
    id: 'fear',
    name: 'Fear',
    description: 'Causes Fear tests in opponents at the specified rating.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'Fear Rating',
    effects: [{ type: 'fear_rating', value: 0 }], // value set from parameter
  },

  flyer: {
    id: 'flyer',
    name: 'Flyer',
    description: 'Can fly. Movement reminder only.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Can fly.' }],
  },

  from_beyond: {
    id: 'from_beyond',
    name: 'From Beyond',
    description: 'Immune to Fear, Pinning, Insanity Points and psychic powers that cloud, control or delude its mind.',
    mechanical: true,
    parameterized: false,
    effects: [{ type: 'condition_immunity', conditions: ['Pn', 'Hl'] }],
  },

  hoverer: {
    id: 'hoverer',
    name: 'Hoverer',
    description: 'Can hover above the ground. Movement reminder only.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Can hover above the ground.' }],
  },

  incorporeal: {
    id: 'incorporeal',
    name: 'Incorporeal',
    description: 'Can only be damaged by Force, Sanctified, or Warp weapons.',
    mechanical: true,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Only damaged by Force, Sanctified, or Warp weapons.' }],
  },

  machine: {
    id: 'machine',
    name: 'Machine',
    description: 'Immune to Stunning, Blood Loss, Fatigue, and Fire. Gains bonus AP to all locations.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'Machine AP',
    effects: [
      { type: 'condition_immunity', conditions: ['S', 'BL', 'BLS', 'F', 'Fi'] },
      // NO flat_ap_bonus here (v6.38.2): Machine's AP is resolved from the
      // trait PARAMETER into `machineAP`, which damageCalc adds alongside
      // `naturalArmour`. A flat_ap_bonus effect would ALSO be summed into
      // naturalArmour and double the armour (Machine(2) soaked +4 AP).
    ],
  },

  multiple_arms: {
    id: 'multiple_arms',
    name: 'Multiple Arms',
    description: 'Has additional limbs. Can make extra attacks or wield additional weapons.',
    mechanical: false,
    parameterized: true,
    parameterLabel: 'Number of Arms',
    effects: [{ type: 'narrative', description: 'Extra limbs allow additional attacks or weapon wielding.' }],
  },

  natural_armour: {
    id: 'natural_armour',
    name: 'Natural Armour',
    description: 'Gains X bonus AP to all locations from natural hide/scales.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'AP Bonus',
    effects: [{ type: 'flat_ap_bonus', value: 0 }], // value set from parameter
  },

  natural_weapons: {
    id: 'natural_weapons',
    name: 'Natural Weapons',
    description: 'Natural melee weapons immune to Power Field destruction.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Immune to Power Field weapon destruction.' }],
  },

  phase: {
    id: 'phase',
    name: 'Phase',
    description: 'Can shift out of phase with reality, passing through solid matter at will.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Can pass through solid matter at will.' }],
  },

  quadruped: {
    id: 'quadruped',
    name: 'Quadruped',
    description: 'Has four or more legs. Movement multiplier increased by one category.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Four+ legs increase movement multiplier by one step.' }],
  },

  regeneration: {
    id: 'regeneration',
    name: 'Regeneration',
    description: 'Regenerates X wounds at the start of each turn during round countdown.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'Wounds per Turn',
    effects: [{ type: 'regeneration', value: 0 }], // value set from parameter
  },

  size: {
    id: 'size',
    name: 'Size',
    description: 'Modifies to-hit based on SIZE_MODS table.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'Size Category',
    effects: [{ type: 'narrative', description: 'Hit modifier from size category.' }],
  },

  strange_physiology: {
    id: 'strange_physiology',
    name: 'Strange Physiology',
    description: 'Does not have normal anatomy. Called shots produce no additional effects.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Non-standard anatomy - called shots have no extra effect.' }],
  },

  stuff_of_nightmares: {
    id: 'stuff_of_nightmares',
    name: 'Stuff of Nightmares',
    description: 'Immune to Blood Loss, Stunning, and Fatigue from critical damage. Only killed at severity 9+.',
    mechanical: true,
    parameterized: false,
    effects: [{ type: 'condition_immunity', conditions: ['BL', 'BLS', 'S', 'F'] }],
  },

  sturdy: {
    id: 'sturdy',
    name: 'Sturdy',
    description: 'Difficult to knock down. +20 bonus to resist being knocked prone or pushed back.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: '+20 to resist knockdown and pushback.' }],
  },

  swift_trait: {
    id: 'swift_trait',
    name: 'Swift',
    description: 'Doubles the base movement rate for its size category.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Movement rate doubled for size category.' }],
  },

  toxic_trait: {
    id: 'toxic_trait',
    name: 'Toxic',
    description: 'Natural attacks inflict Toxic condition. Targets must pass Toughness test or take extra damage.',
    mechanical: true,
    parameterized: true,
    parameterLabel: 'Toxicity Rating',
    effects: [{ type: 'narrative', description: 'Melee hits force Toughness test vs Toxic damage.' }],
  },

  undying: {
    id: 'undying',
    name: 'Undying',
    description: 'This creature cannot die by normal means.',
    mechanical: true,
    parameterized: false,
    effects: [{ type: 'condition_immunity', conditions: ['K', 'Dy'] }],
  },

  unnatural_characteristic: {
    id: 'unnatural_characteristic',
    name: 'Unnatural Characteristic',
    description: 'Sets the unnatural multiplier on a specified characteristic.',
    mechanical: true,
    parameterized: true,
    repeatable: true,
    parameterLabel: 'Characteristic:Multiplier (e.g. tgh:2)',
    effects: [{ type: 'unnatural_characteristic', characteristic: '', multiplier: 2 }],
  },

  warp_instability: {
    id: 'warp_instability',
    name: 'Warp Instability',
    description: 'When taking damage, must pass Willpower test or be banished back to the Warp.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Must pass WP test when damaged or be banished.' }],
  },

  warp_weapons: {
    id: 'warp_weapons',
    name: 'Warp Weapons',
    description: 'Natural weapons count as Warp weapons. Damage ignores mundane armour and Toughness bonus.',
    mechanical: false,
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Natural weapons ignore mundane armour and TB.' }],
  },
};

/** Ordered list of all trait IDs for UI iteration */
export const TRAIT_IDS = Object.keys(TRAIT_CATALOG);

/** Get a trait definition by ID, or undefined if not found */
export function getTrait(id: string): TraitDefinition | undefined {
  return TRAIT_CATALOG[id];
}
